import { useMemo } from "react";
import { bytesToBits } from "@/lib/bitwise";
import { cn } from "@/lib/utils";

interface BitRowProps {
  label: string;
  bytes: Uint8Array;
  color: "a" | "b" | "result";
  maxBits: number;
}

const COLOR_MAP = {
  a: {
    on: "bg-sky-500 dark:bg-sky-400",
    off: "bg-sky-500/10 dark:bg-sky-400/10 border-sky-500/20 dark:border-sky-400/20",
  },
  b: {
    on: "bg-emerald-500 dark:bg-emerald-400",
    off: "bg-emerald-500/10 dark:bg-emerald-400/10 border-emerald-500/20 dark:border-emerald-400/20",
  },
  result: {
    on: "bg-amber-500 dark:bg-amber-400",
    off: "bg-amber-500/10 dark:bg-amber-400/10 border-amber-500/20 dark:border-amber-400/20",
  },
} as const;

function BitRow({ label, bytes, color, maxBits }: BitRowProps) {
  const bits = useMemo(() => bytesToBits(bytes).slice(0, maxBits), [bytes, maxBits]);
  const colors = COLOR_MAP[color];
  const totalBits = bytes.length * 8;
  const truncated = totalBits > maxBits;

  return (
    <div className="flex items-start gap-3">
      <div className="w-14 shrink-0 pt-0.5 text-right">
        <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      </div>
      <div className="flex flex-wrap gap-y-1 overflow-hidden">
        {bits.length === 0 ? (
          <span className="text-xs text-muted-foreground/60 italic">— empty —</span>
        ) : (
          bits.map((bit, i) => {
            const isByteBoundary = i > 0 && i % 8 === 0;
            return (
              <span key={i} className={cn("inline-flex", isByteBoundary && "ml-1.5")}>
                <span
                  className={cn(
                    "inline-block size-3.5 rounded-[3px] border transition-colors",
                    bit ? cn(colors.on, "border-transparent") : colors.off,
                  )}
                />
              </span>
            );
          })
        )}
        {truncated && (
          <span className="ml-2 text-xs text-muted-foreground/60 italic">… +{totalBits - maxBits} more bits</span>
        )}
      </div>
    </div>
  );
}

interface BitVisualizerProps {
  a: Uint8Array;
  b: Uint8Array;
  result: Uint8Array;
  /** Whether B is used as a shift amount rather than data */
  bIsShiftAmount?: boolean;
}

export function BitVisualizer({ a, b, result, bIsShiftAmount }: BitVisualizerProps) {
  const maxBits = 128; // 16 bytes max display

  return (
    <div className="space-y-2.5">
      <BitRow label="A" bytes={a} color="a" maxBits={maxBits} />
      {!bIsShiftAmount && <BitRow label="B" bytes={b} color="b" maxBits={maxBits} />}
      <BitRow label="Result" bytes={result} color="result" maxBits={maxBits} />
    </div>
  );
}
